import type { EngagementChatAttachment, EngagementChatMessageItem } from '@houzy/schemas/partner-cockpit/chat';
import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
    type UseInfiniteQueryResult,
    type UseMutationResult,
} from '@tanstack/react-query';
import { useEffect } from 'react';

import { useTrackEvent } from '../../../lib/trackingEvents';
import {
    fetchChatMessages,
    fetchChatUnseenCount,
    postChatMessage,
    removeChatAttachment,
    uploadChatAttachment,
    type PostChatMessageInput,
} from './chatApi';

const CHAT_POLL_INTERVAL_MS = 5_000; // 5 seconds

export function chatMessagesQueryKey(engagementUuid: string): readonly ['offerRequests', 'chat', string] {
    return ['offerRequests', 'chat', engagementUuid] as const;
}

type ChatMessagesView = {
    messages: EngagementChatMessageItem[];
    canSendMessages: boolean;
};

export function useChatMessages(engagementUuid: string): UseInfiniteQueryResult<ChatMessagesView> & ChatMessagesView {
    const query = useInfiniteQuery({
        queryKey: chatMessagesQueryKey(engagementUuid),
        queryFn: ({ pageParam, signal }) => fetchChatMessages(engagementUuid, pageParam, signal),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (page) => page.nextCursor,
        select: (data) => ({
            messages: data.pages.flatMap((page) => page.items).reverse(),
            canSendMessages: data.pages[0]?.canSendMessages ?? true,
        }),
    });

    // Open until a page says otherwise, so a loading chat does not flash a closed notice.
    return { ...query, messages: query.data?.messages ?? [], canSendMessages: query.data?.canSendMessages ?? true };
}

export function useChatPolling(engagementUuid: string, lastSeenMessageUuid: string | undefined): void {
    const queryClient = useQueryClient();

    const unseenQuery = useQuery({
        queryKey: [...chatMessagesQueryKey(engagementUuid), 'unseen-count', lastSeenMessageUuid],
        queryFn: ({ signal }) => fetchChatUnseenCount(engagementUuid, lastSeenMessageUuid ?? '', signal),
        enabled: lastSeenMessageUuid !== undefined,
        refetchInterval: CHAT_POLL_INTERVAL_MS,
        refetchIntervalInBackground: false,
    });

    const count = unseenQuery.data?.count ?? 0;
    useEffect(() => {
        if (count > 0) {
            void queryClient.invalidateQueries({ queryKey: chatMessagesQueryKey(engagementUuid), exact: true });
        }
    }, [count, engagementUuid, queryClient]);
}

export function usePostChatMessage(
    engagementUuid: string,
    offerRequestId: string,
): UseMutationResult<unknown, Error, PostChatMessageInput> {
    const queryClient = useQueryClient();
    const track = useTrackEvent();
    return useMutation({
        mutationFn: (input) => postChatMessage(engagementUuid, input),
        onSuccess: () => {
            track('Offer Request Chat Message Sent', { offerRequestId });
            void queryClient.invalidateQueries({ queryKey: chatMessagesQueryKey(engagementUuid), exact: true });
        },
    });
}

export function useUploadChatAttachment(
    engagementUuid: string,
): UseMutationResult<EngagementChatAttachment, Error, File> {
    return useMutation({
        mutationFn: (file) => uploadChatAttachment(engagementUuid, file),
    });
}

export function useRemoveChatAttachment(engagementUuid: string): UseMutationResult<void, Error, string> {
    return useMutation({
        mutationFn: (attachmentUuid) => removeChatAttachment(engagementUuid, attachmentUuid),
    });
}
